import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { Button, Divider, Form, Modal } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";
import { DropdownOption } from "@teamprevent/common/questionnaires/model";
import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { ConfirmModal } from "@teamprevent/common/confirmedButton/ConfirmModal";

import { routeIdSelector as customerIdSelector } from "../../../crud/customer/selectors";
import { taskSelector } from "../../selectors";
import { ohCaseSelector } from "../../../crud/ohCase/selectors";
import { generalPractitionerSpecialistApi } from "../../../generalPractitionerSpecialistInfo/gpSpecialistInfoApi";
import { WriteToGPSpecialistModel } from "../../../generalPractitionerSpecialistInfo/models";
import { WRITE_TO_GP_SPECIALIST } from "../../../generalPractitionerSpecialistInfo/actionTypes";
import { writeToGPSpecialist } from "../../../generalPractitionerSpecialistInfo/actions";
import { AddTaskModal } from "../../../tasks/components/AddTaskModal";

export const WriteToGeneralPractitionerSpecialistModal: React.FunctionComponent = () => {

    const [isGPSpecialistModalOpen, setIsGPSpecialistModalOpen] = React.useState<boolean>(true);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [showErrors, setShowErrors] = React.useState<boolean>(false);
    const [gpSpecialistOptions, setGPSpecialistOptions] = React.useState<DropdownOption[]>([]);
    const [gpSpecialistId, setGPSpecialistId] = React.useState<string | undefined>(undefined);
    const [notes, setNotes] = React.useState<string | undefined>(undefined);
    const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = React.useState<boolean>(false);
    const [openAddTaskModal, setOpenAddTaskModal] = React.useState<boolean>(false);

    const customerId = useSelector(customerIdSelector);
    const ohCase = useSelector(ohCaseSelector);
    const employeeRecordId = ohCase.employeeRecordId;
    const task = useSelector(taskSelector);

    React.useEffect(() => {
        if (!!customerId && !!employeeRecordId) {
            (async () => {
                setLoading(true);

                const gpSpecialists = await generalPractitionerSpecialistApi.getAll(customerId, employeeRecordId);
                const options = (gpSpecialists ?? [])
                    .map(gpSpec => ({
                        value: gpSpec.id,
                        text: `${gpSpec.type} - ${gpSpec.name}`
                    }))
                    .sort((a, b) => a.text.localeCompare(b.text));
                setGPSpecialistOptions(options);

                setLoading(false);
            })();
        }
    }, [customerId, employeeRecordId]);

    React.useEffect(() => {
        return resetState;
    }, []);

    const resetState = () => {
        setIsGPSpecialistModalOpen(false);
        setLoading(false);
        setSubmitted(false);
        setShowErrors(false);
        setGPSpecialistOptions([]);
        setGPSpecialistId(null);
        setNotes(null);
        setIsCreateTaskModalOpen(false);
        setOpenAddTaskModal(false);
    };

    const onNotesSnippetChanged = React.useCallback((snippet: string) => {
        setNotes(previousNotes => (previousNotes ? previousNotes + " " : "") + snippet);
    }, [setNotes]);

    const dispatch = useDispatch();

    const onGPSpecialistModalSubmit = async () => {
        setSubmitted(true);
        setShowErrors(true);

        if (gpSpecialistId && ohCase?.id && task?.id) {
            const model: WriteToGPSpecialistModel = { gpSpecialistId, notes };

            dispatch(writeToGPSpecialist(customerId, ohCase.id, task.id, model));

            setIsCreateTaskModalOpen(true);
        }
    };

    const onGPSpecialistModalClose = () => {
        dispatch(navigateToParentRoute());
    };

    const onCreateTaskModalConfirm = () => {
        setOpenAddTaskModal(true);
    };

    const onCreateTaskModalClose = () => {
        dispatch(navigateToParentRoute());
    };

    const onAddTaskModalClose = () => {
        dispatch(navigateToParentRoute());
    };

    return (
        <>
            <Modal open={isGPSpecialistModalOpen}>
                <Modal.Header content={task?.description} />
                <Modal.Content>
                    <a href={`/customers/${customerId}/employeeRecords/${employeeRecordId}/gpSpecialistInfo/create`}>
                        <Button type="button" content="Add GP/Specialist record" />
                    </a>
                    <Divider hidden />
                    <Form loading={!task || loading || gpSpecialistOptions === null}>
                        <Form.Field>
                            <Input.Dropdown
                                value={gpSpecialistId}
                                label="GP/Specialist"
                                showErrors={showErrors}
                                required
                                onChange={setGPSpecialistId}
                                options={gpSpecialistOptions}
                                dynamicOptions
                            />
                        </Form.Field>
                        <TextSnippetSelector onSelect={onNotesSnippetChanged} />
                        <Input.Textarea label="Notes" value={notes} showErrors={submitted} onChange={setNotes} />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <IsLoadingButton requestNames={WRITE_TO_GP_SPECIALIST} content="Action Task" onClick={onGPSpecialistModalSubmit} />
                    <Button content="Cancel" onClick={onGPSpecialistModalClose} />
                </Modal.Actions>
            </Modal>
            <ConfirmModal
                isOpen={isCreateTaskModalOpen}
                promptText="Send letter task has been created, do you require any additional tasks?"
                confirmText="Yes"
                cancelText="No"
                onConfirm={onCreateTaskModalConfirm}
                onClose={onCreateTaskModalClose}
                preventOnCloseAfterSubmit
            />
            <AddTaskModal
                open={openAddTaskModal}
                customerId={task?.customer?.id}
                ohCase={ohCase}
                assignmentOptionRequired={true}
                onClose={onAddTaskModalClose}
            />
        </>
    );
};
